/**
 * shoe-size-localizer.js
 *
 * Converts EU shoe sizes in the Shopify variant picker to the visitor's
 * market-appropriate size system: US, UK, JP, or EU (default/fallback).
 *
 * - Only changes DISPLAYED TEXT — radio values and option values are untouched
 *   so Shopify's cart / URL logic is never affected.
 * - Reads visitor country from window.Shopify.marketCountry (set by theme.liquid
 *   via {{ localization.country.iso_code | json }}).
 * - Infers product gender from the page URL handle so the correct US/UK table
 *   is used (women's vs men's shoe sizing differs).
 * - Re-applies after every variant:update event so morphed picker HTML stays
 *   localised after the customer selects a different variant.
 */
(function () {
  'use strict';

  // ── Country → size system ────────────────────────────────────────────────
  /** Countries that see US sizes */
  const US_COUNTRIES = new Set([
    'US', 'CA', 'MX', 'AU', 'NZ', 'PH',
  ]);
  /** Countries that see UK sizes */
  const UK_COUNTRIES = new Set(['GB', 'IE']);
  /** Countries that see JP/Asia sizes (Mondopoint) */
  const JP_COUNTRIES = new Set([
    'JP', 'CN', 'KR', 'TW', 'HK', 'SG', 'MY', 'TH', 'VN', 'ID',
    'IN', 'PK', 'BD', 'LK', 'MM', 'KH', 'LA', 'BN', 'MO',
  ]);

  // ── Conversion tables ────────────────────────────────────────────────────
  // Sources: ISO 19867 / Mondopoint / common online size charts.
  // Key = EU numeric size (string). Value = localised size label string.

  /** EU → US Women's */
  const EU_US_W = {
    33: '2.5', 34: '3.5', 35: '4.5', 36: '5.5', 37: '6.5',
    38: '7.5', 39: '8.5', 40: '9.5', 41: '10.5', 42: '11.5',
    43: '12', 44: '13', 45: '14',
  };

  /** EU → US Men's */
  const EU_US_M = {
    36: '4', 37: '5', 38: '6', 39: '7', 40: '7.5',
    41: '8', 42: '9', 43: '10', 44: '10.5', 45: '11',
    46: '12', 47: '12.5', 48: '13', 49: '14',
  };

  /** EU → UK Women's */
  const EU_UK_W = {
    33: '1', 34: '1.5', 35: '2.5', 36: '3.5', 37: '4',
    38: '5', 39: '6', 40: '7', 41: '7.5', 42: '8',
    43: '8.5', 44: '9', 45: '10',
  };

  /** EU → UK Men's */
  const EU_UK_M = {
    36: '3.5', 37: '4', 38: '5', 39: '6', 40: '6.5',
    41: '7.5', 42: '8', 43: '9', 44: '9.5', 45: '10.5',
    46: '11', 47: '11.5', 48: '12', 49: '13',
  };

  /**
   * EU → JP (Mondopoint mm).
   * Single table — JP sizing is unisex in practice (just foot length in mm).
   */
  const EU_JP = {
    33: '210', 34: '215', 35: '220', 36: '225', 37: '235',
    38: '240', 39: '245', 40: '250', 41: '260', 42: '265',
    43: '270', 44: '275', 45: '280', 46: '285', 47: '290',
    48: '295', 49: '300',
  };

  // ── EU size label detection ───────────────────────────────────────────────
  /**
   * Matches labels like "EU 38", "EU38", "38 EU", "38EU", "EU 38 / Women's".
   * Capture group 1 = numeric EU size from "EU NN" form.
   * Capture group 2 = numeric EU size from "NN EU" form.
   */
  const EU_RE = /^eu\s*(\d+)\b|^(\d+)\s*eu\b/i;

  /** Extract the EU numeric size from a regex match (handles both capture groups). */
  function euNumFromMatch(m) {
    return parseInt(m[1] || m[2], 10);
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  /** Return the active market country code (upper-case), or null. */
  function getMarketCountry() {
    // Primary: set by theme.liquid via {{ localization.country.iso_code | json }}
    const c = window.Shopify && window.Shopify.marketCountry;
    if (c && typeof c === 'string' && c.length >= 2) return c.toUpperCase();

    // Fallback 1: Shopify's active currency (set by Shopify CDN — always present)
    const currency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (currency === 'USD' || currency === 'CAD' || currency === 'AUD' || currency === 'NZD') return 'US';
    if (currency === 'GBP') return 'GB';
    if (currency === 'JPY' || currency === 'CNY' || currency === 'KRW') return 'JP';

    // Fallback 2: browser locale region (e.g. "en-US" → "US", "ja-JP" → "JP")
    const lang = navigator.language || (navigator.languages && navigator.languages[0]) || '';
    const region = lang.split('-')[1];
    if (region && region.length === 2) return region.toUpperCase();

    return null;
  }

  /** Derive size system for a country code. */
  function systemForCountry(country) {
    if (!country) return 'eu';
    if (US_COUNTRIES.has(country)) return 'us';
    if (UK_COUNTRIES.has(country)) return 'uk';
    if (JP_COUNTRIES.has(country)) return 'jp';
    return 'eu';
  }

  /**
   * Infer product gender from the page URL pathname.
   * Returns 'women' | 'men' | 'kids'.
   */
  function getProductGender() {
    const path = window.location.pathname.toLowerCase();
    if (/\b(women|womens|woman|girls|girl)\b/.test(path)) return 'women';
    if (/\b(kid|kids|youth|child|children|toddler|infant|baby)\b/.test(path)) return 'kids';
    return 'men';
  }

  /**
   * Convert one EU size label to the target system.
   * Returns the new label string, or null if no conversion exists.
   */
  function convertLabel(rawText, system, gender) {
    const m = rawText.match(EU_RE);
    if (!m) return null;
    const eu = euNumFromMatch(m);

    switch (system) {
      case 'us': {
        const table = gender === 'women' ? EU_US_W : EU_US_M;
        const v = table[eu];
        // Use non-breaking space so "US 7.5" doesn't break across lines
        return v != null ? 'US\u00A0' + v : null;
      }
      case 'uk': {
        const table = gender === 'women' ? EU_UK_W : EU_UK_M;
        const v = table[eu];
        return v != null ? 'UK\u00A0' + v : null;
      }
      case 'jp': {
        const v = EU_JP[eu];
        return v != null ? v + '\u00A0cm' : null;
      }
      default:
        return null; // 'eu' → keep original
    }
  }

  /**
   * Translate all size labels inside a given DOM root (a variant-picker element).
   * Only modifies TEXT — never attribute `value` — so Shopify cart/URL logic is safe.
   */
  function translatePicker(root, system, gender) {
    // ── Radio-button style: <span data-key="variant-option-text">EU 38</span> ──
    root.querySelectorAll('[data-key="variant-option-text"]').forEach((span) => {
      // Prefer stored original EU text so re-translating after morph is idempotent
      const original = span.dataset.euOriginal || span.textContent.trim();
      const converted = convertLabel(original, system, gender);
      if (converted !== null) {
        span.dataset.euOriginal = original; // preserve for future re-renders
        span.textContent = converted;
      }
    });

    // Also update the displayed selected-value text inside the legend (swatch style)
    root.querySelectorAll('.variant-option__swatch-value').forEach((span) => {
      const original = span.dataset.euOriginal || span.textContent.trim();
      const converted = convertLabel(original, system, gender);
      if (converted !== null) {
        span.dataset.euOriginal = original;
        span.textContent = converted;
      }
    });

    // ── Dropdown style: <option value="EU 38">EU 38</option> ──
    // Change only textContent, leave value intact.
    root.querySelectorAll('select.variant-option__select option').forEach((opt) => {
      const original = opt.dataset.euOriginal || opt.textContent.trim();
      const converted = convertLabel(original, system, gender);
      if (converted !== null) {
        opt.dataset.euOriginal = original;
        opt.textContent = converted;
      }
    });
  }

  /** Return true if this picker contains at least one EU-format size label. */
  function isShoeVariantPicker(root) {
    const selectors = [
      '[data-key="variant-option-text"]',
      'select.variant-option__select option',
    ].join(', ');
    return Array.from(root.querySelectorAll(selectors)).some((el) =>
      EU_RE.test(el.textContent.trim())
    );
  }

  // ── Initialisation ─────────────────────────────────────────────────────────

  function init() {
    const country = getMarketCountry();
    const system = systemForCountry(country);

    // EU visitors (or unknown market) see the EU sizes already — nothing to do.
    if (system === 'eu') return;

    const gender = getProductGender();

    /** Apply to every variant-picker on the page that has EU shoe sizes. */
    function applyAll() {
      document.querySelectorAll('variant-picker').forEach((picker) => {
        // Check against stored original data OR live text
        if (isShoeVariantPicker(picker)) {
          translatePicker(picker, system, gender);
        }
      });
    }

    // ── Initial pass (page load) ──────────────────────────────────────────
    applyAll();

    // ── Re-apply after the theme morphs the variant picker ────────────────
    // The theme dispatches 'variant:update' (ThemeEvents.variantUpdate) which
    // bubbles up to document after each variant selection re-render.
    document.addEventListener('variant:update', () => {
      // Small rAF delay so morph has finished patching the DOM first
      requestAnimationFrame(applyAll);
    });

    // ── Failsafe: MutationObserver on each picker ─────────────────────────
    // Catches any dynamic renders not covered by the event listener
    // (e.g. quick-add dialogs, featured product sections).
    let debounceTimer = null;
    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(applyAll, 60);
    });

    function observeAllPickers() {
      document.querySelectorAll('variant-picker').forEach((picker) => {
        observer.observe(picker, { childList: true, subtree: true });
      });
    }

    observeAllPickers();

    // Also observe new pickers injected after page load (quick-add, etc.)
    const bodyObserver = new MutationObserver((mutations) => {
      const hasNewPicker = mutations.some((m) =>
        Array.from(m.addedNodes).some(
          (n) => n.nodeType === 1 && (n.tagName === 'VARIANT-PICKER' || n.querySelector?.('variant-picker'))
        )
      );
      if (hasNewPicker) {
        observeAllPickers();
        requestAnimationFrame(applyAll);
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  // Run once DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
